import type { ReactNode } from 'react';
import { createBrowserRouter, Navigate, Outlet, useNavigate } from 'react-router-dom';
import { Spinner } from '@os/ui';
import { AuthCallback, AuthGuard } from '@os/auth-client/react';
import { useMe } from './api';
import { Shell } from './layout/Shell';
import { PageAlert } from './desktop/Page';
import { RouteError } from './pages/RouteError';
import Launcher from './pages/Launcher';
import Account from './pages/Account';
import Users from './pages/admin/Users';
import Apps from './pages/admin/Apps';
import Audit from './pages/admin/Audit';
import Gallery from './pages/dev/Gallery';

function Callback() {
  const nav = useNavigate();
  return <AuthCallback onComplete={(to) => nav(to, { replace: true })} />;
}

/** /admin/* and /dev/ui: the API enforces os:admin too; this keeps non-admins from seeing an empty, erroring page. */
function RequireAdmin({ children }: { children: ReactNode }) {
  const me = useMe();
  if (me.isPending) return <div className="dk-fallback"><Spinner /></div>;
  if (me.error) return <div className="pg"><div className="pg-body"><PageAlert error={me.error} fallback="Could not load your account" /></div></div>;
  if (!me.data?.is_admin) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export const router = createBrowserRouter([
  { path: '/auth/callback', element: <Callback /> },
  {
    path: '/',
    element: <AuthGuard><Shell><Outlet /></Shell></AuthGuard>,
    children: [
      {
        errorElement: <RouteError />,
        children: [
          { index: true, element: <Launcher /> },
          { path: 'account', element: <Account /> },
          { path: 'admin', element: <Navigate to="/admin/users" replace /> },
          { path: 'admin/users', element: <RequireAdmin><Users /></RequireAdmin> },
          { path: 'admin/apps', element: <RequireAdmin><Apps /></RequireAdmin> },
          { path: 'admin/audit', element: <RequireAdmin><Audit /></RequireAdmin> },
          { path: 'dev/ui', element: <RequireAdmin><Gallery /></RequireAdmin> },
          { path: '*', element: <Navigate to="/" replace /> },
        ],
      },
    ],
  },
]);
